/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useState } from "react";
import ModalView, { ModalContent } from "../layout/modal/ModalView";
import ModalTitleHeader from "../layout/modal/ModalTitleHeader";
import { DISABLED, MediumText, SmallText } from "../styles/styles";
import { Column, Row } from "../layout/flexbox";
import { FADE_IN_KEYFRAMES } from "../styles/animations";
import { BLUE_CREEK, DARK_BLUE_CREEK, DARK_GREY, LIGHT_GREY, WHITE } from "../styles/colors";
import download from '../assets/icons/download.svg';
import upload from '../assets/icons/uploadWhite.svg';
import uploadCloud from '../assets/icons/uploadCloud.svg';
import { getResource } from "../language/resources/getResource";
import { useSelector } from "react-redux";
import { IAppState } from "../core/redux/IAppState";
import { ResortMappingObject, RTPMappingProduct, SiriuswareMappingProduct } from "./productMappings";
import { ProductMappingTableUpload } from "./ProductMappingTableUpload";
import checkCircleFilled from '../assets/icons/checkCircleFilled.svg'
import ErrorView from "../forms/ErrorView";
import { css, SerializedStyles } from "@emotion/react";


const mappingUploadImage = css`
  width: 15px;
  margin-right: 5px;
  position: relative;
  top: 2px;
`;

const uploadModalButtonRow = css`
  justify-content: space-around; 
  width: 350px; 
  margin-left: auto;
  margin-right: auto;
  margin-top: 10px;
  margin-bottom: 20px;
`;

const uploadModalInfo = css`
  ${SmallText} 
  margin-bottom: 5px;
`;

const uploadModalInfoWarning = css`
  color: red;
  ${SmallText} 
  margin-bottom: 5px;
  position: relative;
  left: 17px;
`;

const uploadModalUploadArea = (dragging: boolean) => css`
  width: 450px;
  height: 180px;
  border-style: dashed;
  border-color: ${dragging ? '#c3c3c2' : '#e3e3e2'};
  border-width: 5px;
  margin-top: 25px;
  margin-bottom: 5px;
  margin-right: auto;
  margin-left: auto;
  border-radius: 30px;
  text-align: center;
`;

const uploadModalDragText = (dragging: boolean) => css`
  color: ${dragging ? '#c3c3c2' : '#e3e3e2'};
  display: block;
  margin-top: 50px;
  margin-bottom: 15px;
`;

const uploadModalFile = css `
  display: block;
  margin-top: 50px;
  margin-bottom: 15px;
  color: ${BLUE_CREEK};
`;

const uploadModalSuccess = css `
  display: block;
  margin-top: 50px;
  margin-bottom: 15px;
  margin-right: auto;
  margin-left: auto;
`;

const mappingButton = css`
  text-align: center;
  text-decoration: none;
  ${MediumText};
  animation: 1s ${FADE_IN_KEYFRAMES};
  cursor: pointer;

  background-color: ${WHITE};
  border: solid 1px ${BLUE_CREEK};
  padding: 14px 30px;
  color: ${BLUE_CREEK};
  margin-left: 10px;
  margin-right: 5x;

  &:hover {
    background-color: ${LIGHT_GREY};
    color: ${DARK_BLUE_CREEK};
  }
`;

const mappingButtonSubmit = css `
  text-align: center;
  text-decoration: none;
  ${MediumText};
  animation: 1s ${FADE_IN_KEYFRAMES};
  cursor: pointer;

  background-color: ${BLUE_CREEK}; 
  border: solid 1px ${WHITE};
  padding: 14px 30px;
  color: ${WHITE};
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 20px;
  margin-top: 70px;
  width: 200px;
  
  &:hover {
    background-color: ${DARK_BLUE_CREEK}; 
    color: ${WHITE};
  }

  &:disabled {
    ${DISABLED}
  }
`;

const mappingButtonNav = css `
  text-align: center;
  text-decoration: none;
  ${MediumText};
  animation: 1s ${FADE_IN_KEYFRAMES};
  cursor: pointer;

  background-color: ${BLUE_CREEK}; 
  border: solid 1px ${WHITE};
  padding: 14px 30px;
  color: ${WHITE};
  margin-left: 10px;
  margin-right: 0px;
  
  &:hover {
    background-color: ${DARK_BLUE_CREEK}; 
    color: ${WHITE};
  }

  &:disabled {
    background-color: ${LIGHT_GREY};  /* Change background for disabled */
    color: ${DARK_GREY};              /* Change text color for disabled */
    border: solid 1px gray;          /* Change border for disabled */
  }
`;

const navBar = css`
  display: flex;
  height: 40px;
  margin-bottom: 15px;
`;

const navBarItem = (color: string) => css`
  flex-grow: 1;
  text-align: center;
  border: 1px solid gray;
  background-color: ${color};
  line-height: 40px;
  position: relative;
`;

const arrowClass = (color: string) => css `
  width: 0; 
  height: 0; 
  border-top: 20px solid transparent; 
  border-bottom: 20px solid transparent; /* 40px height (20+20) */
  border-left: 20px solid ${color};
  position:absolute;
  right:-20px;
  top:0px;
  z-index: 99;
`

type Props = { 
  title: string,
  icon?: any,
  negativeButtonText?: string,
  negativeButtonTitle?: string,
  onNegativeButtonClick?: () => void,
  onDismiss: () => void,
  uploadCSV: (csvFile: File | undefined) => void,
  downloadCSV: () => void,
  uploadSuccess: boolean,
  uploadError?: string,
  rootCss?: SerializedStyles,
  mappingObject: ResortMappingObject,
}

 export const ProductMappingUploadModal = (props: Props) => { 

  const [csvFile, setCsvFile] = useState<File>();
  const [dragging, setDragging] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [validFile, setValidFile] = useState<boolean>(false);
  const [fileErrorMessage, setFileErrorMessage] = useState('');
  const [previewMappings, setPreviewMappings] = useState<ResortMappingObject>();
  const resources = useSelector((state: IAppState) => state.locale)

  const primaryColor = "#4FC3F7";
  const secondaryColor = "#B3E5FC";

  const isValidCsvFile = useCallback(() => {
    if (csvFile) {
      const isCsv = csvFile.type === 'text/csv' || csvFile.type === 'application/vnd.ms-excel';
      if (isCsv) {
        setValidFile(true);
        setFileErrorMessage('');
        return true;
      } else {
        setValidFile(false);
        setFileErrorMessage(getResource(resources, 'PRODUCT_MAPPING_UPLOAD_WRONG_FILE_TYPE'));
      }
    }
    else {
      setValidFile(false);
      setFileErrorMessage(getResource(resources, 'PRODUCT_MAPPING_UPLOAD_NO_FILE'));
    }
    return false;
  }, [csvFile, resources])

  const handleUpload = () => {
    if (isValidCsvFile()) {
      props.uploadCSV(csvFile);
    }
  };

  const handleFileChange = (event) => setCsvFile(event.target.files[0]);

  const makeMappingsRTP = useCallback(() => {
    const reader = new FileReader();
    const changedProducts: RTPMappingProduct[] = [];
    const allProducts: RTPMappingProduct[] = [];
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      //loop through csv lines
      var found = false;
      var foundError = false;
      const foundIDs : string[] = [];
      lines.forEach((line, index) => {
        if (foundError === false){
          const fields = line.split (',');
          if (index === 0) {
            const entitlementHeader = fields[0];
            const productHeader = fields[5];
            const accessHeader = fields[6];
            const mediaHeader = fields[7];
            if (entitlementHeader !== 'EntitlementTypeMappingID' || productHeader !== 'Product Header Code' || accessHeader !== 'Access Rule' || mediaHeader !== 'Media Product Header Code') {
              setFileErrorMessage(getResource(resources, 'PRODUCT_MAPPING_UPLOAD_WRONG_HEADERS'));
              setValidFile(false);
              foundError = true;
            }
          }
          else {
            if (fields.length >= 8) {
              const oldProduct = (props.mappingObject.productMappings as RTPMappingProduct[]).find(item => String(item.id) === String(fields[0]));              
              if (oldProduct) {
                found = true;
                if (fields[0] !== undefined && fields[0] !== "" ) { 
                  if (foundIDs.includes(fields[0])) {
                    setFileErrorMessage(getResource(resources, 'PRODUCT_MAPPING_UPLOAD_DUPLICATE_IDS'))
                    setValidFile(false);
                    foundError = true;
                  }
                  foundIDs.push(fields[0]) 
                }
                const product: RTPMappingProduct = {
                  id: fields[0],
                  product: oldProduct.product,
                  season: oldProduct.season,
                  ageBin: oldProduct.ageBin,
                  fulfillmentSeason: oldProduct.fulfillmentSeason,
                  productHeaderCode: fields[5] === oldProduct.productHeaderCode ? fields[5] : oldProduct.productHeaderCode + " -> " + fields[5],
                  accessRule: fields[6] === oldProduct.accessRule ? fields[6] : oldProduct.accessRule + " -> " + fields[6],
                  mediaProductHeaderCode: fields[7] === oldProduct.mediaProductHeaderCode ? fields[7] : oldProduct.mediaProductHeaderCode + " -> " + fields[7],
                  isError: false,
                  isResortConfigurable: true,
                  errorDescription: "",
                  entitlementTypeId: oldProduct.entitlementTypeId,
                  fulfillmentSeasonId: oldProduct.fulfillmentSeasonId,
                  hydrationErrors: undefined,
                };
                if (oldProduct.productHeaderCode !== fields[5] || oldProduct.accessRule !== fields[6]  || oldProduct.mediaProductHeaderCode !== fields[7]) {
                  changedProducts.push(product);
                }
                allProducts.push(product);
              }
            }
            else if (line !== "") {
              setFileErrorMessage(getResource(resources, 'PRODUCT_MAPPING_UPLOAD_NOT_ENOUGH_COLUMNS'));
              foundError = true;
              setValidFile(false);
            }
          }
        }
      });
      if (foundError === false) {
        if (changedProducts.length > 0){
          const mappings: ResortMappingObject = {
            productMappings: allProducts,
            posProvider: props.mappingObject.posProvider,
            hasError: false,
          }
          setPreviewMappings(mappings);
        }
        else if (!found) {
          setFileErrorMessage(getResource(resources, 'PRODUCT_MAPPING_UPLOAD_NO_MATCH'));
          setValidFile(false);
        }
        else {
          setFileErrorMessage(getResource(resources, 'PRODUCT_MAPPING_UPLOAD_NO_CHANGES'));
          setValidFile(false);
        }
      }
    }
    reader.readAsText(csvFile as File);
  }, [csvFile, props.mappingObject.posProvider, props.mappingObject.productMappings, resources]);

  const makeMappingsSW = useCallback(() => {
    const reader = new FileReader();
    const changedProducts: SiriuswareMappingProduct[] = [];
    const allProducts: SiriuswareMappingProduct[] = [];
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      var found = false;
      var foundError = false;
      const foundIDs : string[] = [];
      //loop through csv lines
      lines.forEach((line, index) => {
        if (foundError === false) {
          const fields = line.split (',');
          if (index === 0) {
            const entitlementHeader = fields[0];
            const departmentHeader = fields[5];
            const categoryHeader = fields[6];
            const itemHeader = fields[7];
            if (entitlementHeader !== 'EntitlementTypeMappingID' || departmentHeader !== 'Department' || categoryHeader !== 'Category' || itemHeader !== 'Item') {
              setFileErrorMessage(getResource(resources, 'PRODUCT_MAPPING_UPLOAD_WRONG_HEADERS'));
              foundError = true;
              setValidFile(false);
            }
          }
          else {         
            if (fields.length >= 8) {
              const oldProduct = (props.mappingObject.productMappings as SiriuswareMappingProduct[]).find(item => String(item.id) === String(fields[0]));
              if (oldProduct) {
                found = true;
                if (fields[0] !== undefined && fields[0] !== "" ) { 
                  if (foundIDs.includes(fields[0])) {
                    setFileErrorMessage(getResource(resources, 'PRODUCT_MAPPING_UPLOAD_DUPLICATE_IDS'))
                    setValidFile(false);
                    foundError = true;
                  }
                  foundIDs.push(fields[0]) 
                }
                const product: SiriuswareMappingProduct = {
                  id: fields[0],
                  product: oldProduct.product,
                  season: oldProduct.season,
                  ageBin: oldProduct.ageBin,
                  fulfillmentSeason: oldProduct.fulfillmentSeason,
                  department: fields[5] === oldProduct.department ? fields[5] : oldProduct.department + " -> " + fields[5],
                  category: fields[6] === oldProduct.category ? fields[6] : oldProduct.category + " -> " + fields[6],
                  item: fields[7] === oldProduct.item ? fields[7] : oldProduct.item + " -> " + fields[7],
                  isError: false,
                  isResortConfigurable: true,
                  errorDescription: "",
                  productType: "PASS",
                  wtpReload: false,
                  hydrationErrors: undefined,
                }
                if (oldProduct.department !== fields[5] || oldProduct.category !== fields[6] || oldProduct.item !== fields[7]) {
                  changedProducts.push(product);
                }
                allProducts.push(product);
              }
              else if (line !== "") {
                setFileErrorMessage(getResource(resources, 'PRODUCT_MAPPING_UPLOAD_NOT_ENOUGH_COLUMNS'));
                foundError = true;
                setValidFile(false);
              }           
            }
          }
        }
      });
      if (foundError === false) { 
        if (changedProducts.length > 0){
          const mappings: ResortMappingObject = {
            productMappings: allProducts,
            posProvider: props.mappingObject.posProvider,
            hasError: false,
          }
          setPreviewMappings(mappings);
        }
        else if (!found) {
          setFileErrorMessage(getResource(resources, 'PRODUCT_MAPPING_UPLOAD_NO_MATCH'));
          setValidFile(false);
        }
        else {
          setFileErrorMessage(getResource(resources, 'PRODUCT_MAPPING_UPLOAD_NO_CHANGES'));
          setValidFile(false);
        }
      }
    }
    reader.readAsText(csvFile as File);
  }, [csvFile, props.mappingObject.posProvider, props.mappingObject.productMappings, resources]);

  useEffect(() => {
    if (isValidCsvFile()) {
      if (props.mappingObject.posProvider === 'RTP') {
        makeMappingsRTP();
      }
      else {
        makeMappingsSW();
      }
    }
  }, [csvFile, isValidCsvFile, makeMappingsRTP, makeMappingsSW, props.mappingObject.posProvider, resources])

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragging(true);
  }

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragging(false);
  }

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragging(false);

    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      setCsvFile(droppedFiles[0]);
    }
  }

    return (
      <ModalView onDismiss={props.onDismiss} rootCss={props.rootCss}>
        <ModalTitleHeader
          resources={resources}
          heading={props.title}
          icon={props.icon}
          onDismiss={props.onDismiss}
        />
        <div css={navBar}>
          <span css={page === 1 ? navBarItem(primaryColor) : page === 2 ? navBarItem(secondaryColor) : navBarItem("white")}>
            {getResource(resources, "PRODUCT_MAPPING_UPLOAD_ADD_FILE")}
            <div css={page === 1 ? arrowClass(primaryColor) : page === 2 ? arrowClass(secondaryColor) : arrowClass("white")}/>
          </span>
          <span css={page === 2 ? navBarItem(primaryColor) : navBarItem(secondaryColor)}>
            {getResource(resources, "PRODUCT_MAPPING_UPLOAD_REVIEW")}
            <div css={page === 2 ? arrowClass(primaryColor) : arrowClass(secondaryColor)}/>
          </span>
          <span css={page === 1 ? navBarItem("white") : page === 3 ? navBarItem(primaryColor) : navBarItem(secondaryColor)}>
            {getResource(resources, "PRODUCT_MAPPING_UPLOAD_UPLOAD")}
          </span>
        </div>
        <ModalContent css={css`min-height: 200px;`}>
          {
            // PAGE NUMBER 1
            page === 1 && (
              <Column flexGrow={1} rootCss={css`min-height: 200px;`}>
                <span css={uploadModalInfo}>
                  1. {getResource(resources, "PRODUCT_MAPPING_UPLOAD_ONE")}
                </span>
                <span css={uploadModalInfoWarning}>
                  {getResource(resources, "PRODUCT_MAPPING_UPLOAD_ONE_WARNING")}
                </span>
                <span css={uploadModalInfo}>
                  2. {getResource(resources, "PRODUCT_MAPPING_UPLOAD_TWO")}
                </span>
                <span css={uploadModalInfoWarning}>
                  {getResource(resources, "PRODUCT_MAPPING_UPLOAD_TWO_WARNING")}
                </span>
                <span css={uploadModalInfo}>
                  3. {getResource(resources, "PRODUCT_MAPPING_UPLOAD_THREE")}
                </span>
                <span css={uploadModalInfo}>
                  4. {getResource(resources, "PRODUCT_MAPPING_UPLOAD_FOUR")}
                </span>
                <Row rootCss={uploadModalButtonRow}>
                  <button type="button" onClick={props.downloadCSV} title={"Download"} css={mappingButton}>
                    <img css={mappingUploadImage} src={download} alt="" />
                    <span css={css`${MediumText};`}>
                      {getResource(resources, "PRODUCT_MAPPING_UPLOAD_DOWNLOAD")}
                    </span>
                  </button>
                </Row>
                <div>
                  <span>
                    {getResource(resources, "PRODUCT_MAPPING_UPLOAD_CHOOSE_FILE" )} : {" "}
                  </span>
                  <input css={css`color: ${WHITE};`} 
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                  />
                </div>
                <div
                  css={uploadModalUploadArea(dragging)}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {csvFile ? (
                    <span css={uploadModalFile}>{csvFile.name}</span>
                  ) : (
                    <span css={uploadModalDragText(dragging)}>
                      {getResource(resources, "PRODUCT_MAPPING_UPLOAD_INSTRUCTION")}
                    </span>
                  )}
                  {!csvFile && !fileErrorMessage && (
                    <img src={uploadCloud} css={css`width: 75px;`}></img>
                  )}
                  {fileErrorMessage !== undefined && fileErrorMessage !== "" && (
                    <span css={css`color: red;`}>{fileErrorMessage}</span>
                  )}
                </div>
              </Column>
            )
          }
          {
            //PAGE NUMBER 2
            page === 2 && (
              <Column>
                <span css={css`margin-bottom: 5px; color: ${BLUE_CREEK};`}>
                  {getResource(resources, "PRODUCT_MAPPING_UPLOAD_FILE_INFO")}{" "}
                  {(csvFile as File).name}
                </span>
                {previewMappings && (
                  <ProductMappingTableUpload
                    resortMappingObject={previewMappings}
                  />
                )}
              </Column>
            )
          }
          {
            //PAGE NUMBER 3
            page === 3 && (
              <Column>
                <span css={css`margin-bottom: 5px; color: ${BLUE_CREEK};`}>
                  {getResource(resources, "PRODUCT_MAPPING_UPLOAD_FILE_INFO")}{" "}
                  {(csvFile as File).name}
                </span>
                <button 
                  type="submit" 
                  disabled={props.uploadError !== undefined || props.uploadSuccess}
                  onClick={handleUpload} 
                  title={"Upload"} 
                  css={mappingButtonSubmit}>
                    <img css={mappingUploadImage} src={upload} alt="" />
                    <span css={css`${MediumText};`}>{getResource(resources, "PRODUCT_MAPPING_UPLOAD_UPLOAD")}</span>
                </button>
                {props.uploadSuccess && (
                  <span css={uploadModalSuccess}>
                    {getResource(resources, "PRODUCT_MAPPING_UPLOAD_SUCCESS")}
                    <img css={css`position: relative; top: 5px;`} src={checkCircleFilled} alt=""/>
                  </span>
                )}
                {/* Upload Error Message */}
                <ErrorView error={props.uploadError}/>
              </Column>
            )
          }
        </ModalContent>
        <Row alignItems="baseline" justifyContent="flex-end" rootCss={css` margin-bottom: 15px; margin-right: 15px;`}>
          {page > 1 && !props.uploadSuccess && props.uploadError === undefined && (
            <button 
              type="button" 
              onClick={() => setPage(page - 1)} 
              title={getResource(resources, "BULK_UPLOAD_BACK")} 
              css={mappingButton}>
              {getResource(resources, "BULK_UPLOAD_BACK")}
            </button>
          )}
          {page < 3 && (
            <button
              type="button"
              disabled={!validFile}
              onClick={() => setPage(page + 1)}
              title={getResource(resources, "BULK_UPLOAD_NEXT")}
              css={mappingButtonNav}
            >
              {getResource(resources, "BULK_UPLOAD_NEXT")}
            </button>
          )}
          {props.negativeButtonText && (
            <button 
              type="button" 
              onClick={props.onNegativeButtonClick} 
              title={props.negativeButtonTitle} 
              css={mappingButton}>
                {props.negativeButtonText}
            </button>
          )}
        </Row>
      </ModalView>
    );
 }
